import Image from 'next/image';
import { Dispatch, SetStateAction } from 'react';

import { CapitalRequestInput } from '@scaleup/models';
import { Button } from '../../../ui';

export const Breakdown = ({
  capitalRequestInput,
  handleChange,
  setCurrentStep,
  isLoading,
}: {
  capitalRequestInput: CapitalRequestInput;
  setCurrentStep: Dispatch<
    SetStateAction<
      | 'REQUIREMENTS'
      | 'INTRODUCTION'
      | 'CAPITAL TYPES'
      | 'GROWTH PLAN'
      | 'FINANCIALS'
      | 'OWNERSHIP'
      | 'EBITDA'
      | 'AMOUNT NEEDED'
      | 'BREAKDOWN'
      | 'REVIEW'
      | 'CONFIRMATION'
    >
  >;
  handleChange: (
    key: string,
    value: { amount: number; description: string }[]
  ) => void;
  isLoading: boolean;
}) => {
  const capitalNeedListTotal = capitalRequestInput.capitalNeedList.reduce(
    (sum, currentCapitalNeed) => sum + currentCapitalNeed.amount,
    0
  );

  return (
    <div className="flex gap-8">
      <div className="flex flex-col gap-4 flex-1">
        <div className="flex flex-col gap-2">
          <span className="text-2xl font-bold">
            The uses of the money broken down
          </span>
          <span className="text-base font-normal">
            Complete detailed request by entering amounts in the following
            categories:
          </span>
        </div>
        <div className="flex gap-4">
          <div className="flex-1 flex flex-col gap-2">
            Build Inventory
            <div className="flex items-center gap-2 bg-gray-100 rounded-2xl px-4 py-2">
              <Image
                src="/icons/currency.svg"
                alt="Prefix Icon"
                width={20}
                height={20}
              />
              <input
                type="text"
                className="w-28 bg-gray-100 focus:bg-none focus:border-none"
                placeholder="Enter amount"
                value={capitalRequestInput?.capitalNeedList[0]?.amount}
                onChange={(e) => {
                  const capitalNeedList = capitalRequestInput?.capitalNeedList;
                  if (capitalNeedList[0]) {
                    capitalNeedList[0].amount = Number(e.target.value);
                  }
                  handleChange('capitalNeedList', capitalNeedList);
                }}
              />
            </div>
          </div>
          <div className="flex-1 flex flex-col gap-2">
            Buy another company
            <div className="flex items-center gap-2 bg-gray-100 rounded-2xl px-4 py-2">
              <Image
                src="/icons/currency.svg"
                alt="Prefix Icon"
                width={20}
                height={20}
              />
              <input
                type="text"
                className="w-28 bg-gray-100 focus:bg-none focus:border-none"
                placeholder="Enter amount"
                value={capitalRequestInput?.capitalNeedList[1]?.amount}
                onChange={(e) => {
                  const capitalNeedList = capitalRequestInput?.capitalNeedList;
                  if (capitalNeedList && capitalNeedList[1]) {
                    capitalNeedList[1].amount = Number(e.target.value);
                  }
                  handleChange('capitalNeedList', capitalNeedList);
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex-1 flex flex-col gap-2">
            Buy new equipment
            <div className="flex items-center gap-2 bg-gray-100 rounded-2xl px-4 py-2">
              <Image
                src="/icons/currency.svg"
                alt="Prefix Icon"
                width={20}
                height={20}
              />
              <input
                type="text"
                className="w-28 bg-gray-100 focus:bg-none focus:border-none"
                placeholder="Enter amount"
                value={capitalRequestInput?.capitalNeedList[2]?.amount}
                onChange={(e) => {
                  const capitalNeedList = capitalRequestInput?.capitalNeedList;
                  if (capitalNeedList && capitalNeedList[2]) {
                    capitalNeedList[2].amount = Number(e.target.value);
                  }
                  handleChange('capitalNeedList', capitalNeedList);
                }}
              />
            </div>
          </div>
          <div className="flex-1 flex flex-col gap-2">
            Finance receivables
            <div className="flex items-center gap-2 bg-gray-100 rounded-2xl px-4 py-2">
              <Image
                src="/icons/currency.svg"
                alt="Prefix Icon"
                width={20}
                height={20}
              />
              <input
                type="text"
                className="w-28 bg-gray-100 focus:bg-none focus:border-none"
                placeholder="Enter amount"
                value={capitalRequestInput?.capitalNeedList[3]?.amount}
                onChange={(e) => {
                  const capitalNeedList = capitalRequestInput?.capitalNeedList;
                  if (capitalNeedList && capitalNeedList[3]) {
                    capitalNeedList[3].amount = Number(e.target.value);
                  }
                  handleChange('capitalNeedList', capitalNeedList);
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex-1 flex flex-col gap-2">
            Hire operations people
            <div className="flex items-center gap-2 bg-gray-100 rounded-2xl px-4 py-2">
              <Image
                src="/icons/currency.svg"
                alt="Prefix Icon"
                width={20}
                height={20}
              />
              <input
                type="text"
                className="w-28 bg-gray-100 focus:bg-none focus:border-none"
                placeholder="Enter amount"
                value={capitalRequestInput?.capitalNeedList[4]?.amount}
                onChange={(e) => {
                  const capitalNeedList = capitalRequestInput?.capitalNeedList;
                  if (capitalNeedList && capitalNeedList[4]) {
                    capitalNeedList[4].amount = Number(e.target.value);
                  }
                  handleChange('capitalNeedList', capitalNeedList);
                }}
              />
            </div>
          </div>
          <div className="flex-1 flex flex-col gap-2">
            Hire sales people
            <div className="flex items-center gap-2 bg-gray-100 rounded-2xl px-4 py-2">
              <Image
                src="/icons/currency.svg"
                alt="Prefix Icon"
                width={20}
                height={20}
              />
              <input
                type="text"
                className="w-28 bg-gray-100 focus:bg-none focus:border-none"
                placeholder="Enter amount"
                value={capitalRequestInput?.capitalNeedList[5]?.amount}
                onChange={(e) => {
                  const capitalNeedList = capitalRequestInput?.capitalNeedList;
                  if (capitalNeedList && capitalNeedList[5]) {
                    capitalNeedList[5].amount = Number(e.target.value);
                  }
                  handleChange('capitalNeedList', capitalNeedList);
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-4">
          <div className="flex-1 flex flex-col gap-2">
            Purchase property
            <div className="flex items-center gap-2 bg-gray-100 rounded-2xl px-4 py-2">
              <Image
                src="/icons/currency.svg"
                alt="Prefix Icon"
                width={20}
                height={20}
              />
              <input
                type="text"
                className="w-28 bg-gray-100 focus:bg-none focus:border-none"
                placeholder="Enter amount"
                value={capitalRequestInput?.capitalNeedList[6]?.amount}
                onChange={(e) => {
                  const capitalNeedList = capitalRequestInput?.capitalNeedList;
                  if (capitalNeedList && capitalNeedList[6]) {
                    capitalNeedList[6].amount = Number(e.target.value);
                  }
                  handleChange('capitalNeedList', capitalNeedList);
                }}
              />
            </div>
          </div>
          <div className="flex-1 flex flex-col gap-2">
            Refinance/distribute to owner&#40;s&#41;
            <div className="flex items-center gap-2 bg-gray-100 rounded-2xl px-4 py-2">
              <Image
                src="/icons/currency.svg"
                alt="Prefix Icon"
                width={20}
                height={20}
              />
              <input
                type="text"
                className="w-28 bg-gray-100 focus:bg-none focus:border-none"
                placeholder="Enter amount"
                value={capitalRequestInput?.capitalNeedList[7]?.amount}
                onChange={(e) => {
                  const capitalNeedList = capitalRequestInput?.capitalNeedList;
                  if (capitalNeedList && capitalNeedList[7]) {
                    capitalNeedList[7].amount = Number(e.target.value);
                  }
                  handleChange('capitalNeedList', capitalNeedList);
                }}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          <span className="font-bold">
            Total requested: ${capitalRequestInput.capitalNeed}
          </span>
          <span className="font-bold">
            Total Breakdown: ${capitalNeedListTotal}
          </span>
        </div>
        {Number(capitalRequestInput.capitalNeed) !== capitalNeedListTotal && (
          <div className="flex gap-3 bg-primary-25 border border-primary-300 rounded-xl p-4">
            <Image
              src={'/icons/info-circle.svg'}
              alt="Info Icon"
              width={20}
              height={20}
            />
            <span className="text-primary-700 font-bold">
              The total breakdown should match the total requested amount.
            </span>
          </div>
        )}
        <div className="flex gap-3">
          <div>
            <Button
              text="Back"
              variant={isLoading ? 'disabled' : 'secondary'}
              onClick={() => setCurrentStep('AMOUNT NEEDED')}
            />
          </div>
          <div>
            <Button
              text="Continue"
              variant={
                isLoading ||
                Number(capitalRequestInput.capitalNeed) !== capitalNeedListTotal
                  ? 'disabled'
                  : 'primary'
              }
              suffixIcon="/icons/arrow_right.svg"
              onClick={() => setCurrentStep('REVIEW')}
            />
          </div>
        </div>
      </div>
      <div className="flex flex-1 justify-center">
        <Image
          src="/create_cr.png"
          alt="Supplier Request Image"
          priority
          width={512}
          height={584}
          style={{ objectFit: 'contain' }}
        />
      </div>
    </div>
  );
};
